import type { TransformedSimpleRecordDto } from '../api/@types/enhanced-v4-api.types';
import type { HierarchyValue } from '../api/@types/hierarchy.types';

/**
 * The record's hierarchy value captions joined (e.g. "Site A" or "Site A, North"), or '' if none.
 * Values are ordered by hierarchy type id — fine for the single-level POC; multi-level ordering
 * belongs with the 14084 unification.
 */
export const getRecordHierarchyLabel = (record: TransformedSimpleRecordDto): string =>
  Object.values(record.hierarchyValues ?? {})
    // Order explicitly by hierarchy type id rather than relying on object key iteration order.
    .sort((a, b) => (a?.hierarchyTypeId ?? 0) - (b?.hierarchyTypeId ?? 0))
    .map((value) => value?.caption)
    .filter((caption): caption is string => Boolean(caption))
    .join(', ');

/**
 * Picker row text: the record's hierarchy value captions, prefixed to the risk scenario
 * (e.g. "Site A · Fall from height") so the hierarchy leads each row and is easy to scan.
 * Falls back to whichever part is present so a blank scenario never leaves a dangling separator.
 */
export const buildRecordDisplayText = (record: TransformedSimpleRecordDto, scenarioFieldId: number): string => {
  const scenario = String(record.fields?.[scenarioFieldId] ?? '').trim();
  const hierarchy = getRecordHierarchyLabel(record);
  if (!hierarchy) return scenario;
  if (!scenario) return hierarchy;
  return `${hierarchy} · ${scenario}`;
};

/**
 * Sorts records so those in the same hierarchy cluster together (by hierarchy caption, then scenario);
 * records with no hierarchy sort last. Client-side, so it groups within the loaded page — which covers
 * the typical single-page bowtie list; true cross-page grouping is MYOSH-14279.
 */
export const sortRecordsByHierarchy = (
  records: TransformedSimpleRecordDto[],
  scenarioFieldId: number
): TransformedSimpleRecordDto[] =>
  [...records].sort((a, b) => {
    const hierarchyA = getRecordHierarchyLabel(a);
    const hierarchyB = getRecordHierarchyLabel(b);
    if (hierarchyA !== hierarchyB) {
      if (!hierarchyA) return 1;
      if (!hierarchyB) return -1;
      return hierarchyA.localeCompare(hierarchyB);
    }
    // Trim to match buildRecordDisplayText so the sort order lines up with the rendered text.
    const scenarioA = String(a.fields?.[scenarioFieldId] ?? '').trim();
    const scenarioB = String(b.fields?.[scenarioFieldId] ?? '').trim();
    return scenarioA.localeCompare(scenarioB);
  });

/**
 * Formats the selected hierarchy value ids for one type into a compact label plus a tooltip of the
 * resolved captions: the captions when resolvable ("Site A, Site B +1"), otherwise a "N selected"
 * fallback — used while the values are still loading or the selection is outside the page-capped values
 * query, so the indicator never renders a dangling label with no values. Ids with no resolved caption
 * are still counted in the "+N" but aren't listed in the tooltip.
 */
export const formatSelectedValues = (
  valueIds: number[],
  values: Pick<HierarchyValue, 'id' | 'caption'>[],
  maxShown: number
): { text: string; title: string } => {
  const captionById = new Map(values.map((value) => [value.id, value.caption]));
  const captions = valueIds.map((id) => captionById.get(id)).filter((caption): caption is string => Boolean(caption));

  if (captions.length === 0) {
    return { text: `${valueIds.length} selected`, title: '' };
  }

  const shownCaptions = captions.slice(0, maxShown);
  // Count overflow against the full selection, not just resolved captions, so unresolved selections
  // (still loading, or beyond the page cap) are still reflected in the "+N".
  const hidden = valueIds.length - shownCaptions.length;
  const text = hidden > 0 ? `${shownCaptions.join(', ')} +${hidden}` : shownCaptions.join(', ');
  return { text, title: captions.join(', ') };
};
